package horcrux.frontend.common.views.collections.importdocuments

import horcrux.common.api.dtos.types.CollectionMetadata
import horcrux.common.api.dtos.types.CollectionName
import horcrux.common.api.dtos.types.DocumentMetadata
import horcrux.common.utils.invert
import horcrux.common.utils.safelet.safeLet
import horcrux.common.utils.toOptional
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.MaybeState
import horcrux.frontend.common.State
import horcrux.frontend.common.ViewRefreshState
import horcrux.frontend.common.views.chat.EditInputPromptWithHelp
import horcrux.frontend.common.views.chat.InputPrompt
import horcrux.frontend.common.views.collections.CollectionMetadataByID
import horcrux.frontend.common.views.collections.CollectionNavigatorState
import horcrux.frontend.common.views.collections.ImportButtonLabel
import horcrux.frontend.common.views.collections.collectiondialogs.CollectionDetailsState
import horcrux.frontend.common.views.components.containers.DialogAction
import horcrux.frontend.common.views.components.containers.DialogView
import horcrux.frontend.common.views.components.containers.HStack
import horcrux.frontend.common.views.components.containers.VStack
import mui.icons.material.Settings
import mui.material.AlertColor
import mui.material.DialogContent
import mui.material.DialogTitle
import mui.material.IconButton
import mui.system.sx
import react.FC
import react.Props
import react.useEffect
import web.cssom.AlignItems

external interface CreateCollectionAndImportDocumentDialogProps : Props {
    var showDialogState: BooleanState
    var currentCollectionMetadataMaybeState: State<CollectionMetadata?>
    var collectionMetadataByID: CollectionMetadataByID
    var documentMetadataList: List<DocumentMetadata>
    var collectionNavigatorState: CollectionNavigatorState
    var updateCollectionMetadata: (CollectionMetadata) -> Unit
    var closeDialog: () -> Unit
    var globalAppState: GlobalAppState
    var viewRefreshState: ViewRefreshState
}

val CreateCollectionAndImportDocumentDialog = FC<CreateCollectionAndImportDocumentDialogProps> { props ->
    val documentInputDataByClientSideIDState = props.globalAppState.documentInputDataByClientSideIDState
    val inputPromptMaybeState = MaybeState<InputPrompt>()
    val importButtonLabelState = props.collectionNavigatorState.importButtonLabelState
    val showEditEmbeddingModelConfigsDialogState = BooleanState()
    val collectionDetailsState = CollectionDetailsState()
    val documentInputIsInvalid =
        { documentInputDataByClientSideIDState.get().any { it.value.documentInputMaybe.isInvalid() } }
    val closeDialog = {
        documentInputDataByClientSideIDState.reset()
        collectionDetailsState.reset()
        props.closeDialog()
    }

    // Set defaultCollectionName to be first non-null DocumentName
    useEffect(documentInputDataByClientSideIDState.get()) {
        documentInputDataByClientSideIDState
            .get()
            .toList()
            .sortedBy { it.first.value }
            .map { it.second }
            .firstOrNull { it.documentInputMaybe.valueMaybe != null }
            ?.let {
                collectionDetailsState.defaultCollectionNameMaybe = CollectionName(it.documentName.value)
            }
    }

    EditEmbeddingModelConfigsDialog {
        this.showDialogState = showEditEmbeddingModelConfigsDialogState
        this.embeddingModelConfigsState = props.globalAppState.embeddingModelConfigsState
    }

    DialogView {
        val title = "Create a Collection and Import Documents"
        this.title = title
        this.showDialogState = props.showDialogState
        this.onCloseMaybe = { closeDialog() }
        this.dialogActionsMaybe = listOf(
            DialogAction("Cancel") { _, _ -> closeDialog() },
            DialogAction("Submit") { _, isLoadingState ->
                val documentInputDataByClientSideID = documentInputDataByClientSideIDState.get()
                safeLet(
                    collectionDetailsState.collectionNameMaybeState.get(),
                    documentInputIsInvalid().toOptional().invert()
                ) { collectionName, _ ->
                    when (importButtonLabelState.get()) {
                        ImportButtonLabel.CREATE_COLLECTION_AND_IMPORT -> props.globalAppState.createCollectionAndImportDocuments(
                            collectionName = collectionName,
                            collectionMediaType = collectionDetailsState.collectionMediaTypeState.get(),
                            documentInputDataByClientSideID = documentInputDataByClientSideID,
                            inputPromptMaybeState = inputPromptMaybeState,
                            isLoadingState = isLoadingState,
                            viewRefreshState = props.viewRefreshState,
                            closeDialog = { closeDialog() },
                            updateCollectionMetadata = props.updateCollectionMetadata,
                        )

                        ImportButtonLabel.IMPORT -> props.currentCollectionMetadataMaybeState.get()
                            ?.let { collectionMetadata ->
                                props.globalAppState.importDocuments(
                                    collectionMetadata = collectionMetadata,
                                    documentInputDataByClientSideID = documentInputDataByClientSideID,
                                    inputPromptMaybeState = inputPromptMaybeState,
                                    globalAppState = props.globalAppState,
                                    isLoadingState = isLoadingState,
                                    viewRefreshState = props.viewRefreshState,
                                    closeDialog = { closeDialog() },
                                    updateCollectionMetadata = props.updateCollectionMetadata,
                                )
                            }
                    }
                } ?: run {
                    props.globalAppState.updateBanner(AlertColor.error, "Please ensure all inputs are valid")
                }
            },
        )
        DialogTitle { +title }
        DialogContent {
            VStack {
                this.sx {
                    this.alignItems = AlignItems.center
                }
                CreateDocumentOptionSelector {
                    this.id = "create-document-option-selector"
                    this.currentSelection = importButtonLabelState.get()
                    this.onClick = { importButtonLabel, _ -> importButtonLabelState.set(importButtonLabel) }
                }

                HStack {
                    DocumentInputTypeToggle {
                        this.documentInputDataByClientSideIDState = documentInputDataByClientSideIDState
                    }
                    IconButton {
                        Settings {}
                        this.onClick = { showEditEmbeddingModelConfigsDialogState.setTrue() }
                    }
                }

                DocumentInputs {
                    this.documentInputDataByClientSideIDState = documentInputDataByClientSideIDState
                    this.globalAppState = props.globalAppState
                }

                CollectionDetailsInput {
                    this.importButtonLabelState = importButtonLabelState
                    this.currentCollectionMetadataMaybeState = props.currentCollectionMetadataMaybeState
                    this.collectionNameMaybeState = collectionNameMaybeState
                    this.collectionMetadataByID = props.collectionMetadataByID
                    this.collectionMediaTypeState = collectionMediaTypeState
                    this.collectionDetailsState = collectionDetailsState
                    this.globalAppState = props.globalAppState
                }

                EditInputPromptWithHelp {
                    this.textFieldValueMaybeState = inputPromptMaybeState
                }
            }
        }
    }
}
